import React, { useMemo } from 'react';

import { Autocomplete, TextField, Chip, styled } from '@mui/material';

import { BsChevronDown } from 'react-icons/bs';
import { IoCheckmark } from 'react-icons/io5';

import { IOMultiDropdownProps, TMultiSelectOption } from '@src/types';

import styles from './OMultiDropdown.module.scss';

interface StyledAutocompleteProps {
  height?: string | number;
}

const StyledAutocomplete = styled(Autocomplete<TMultiSelectOption, true>, {
  shouldForwardProp: (prop) => prop !== 'height',
})<StyledAutocompleteProps>(({ height }) => ({
  backgroundColor: 'white',

  '& .MuiOutlinedInput-root': {
    borderRadius: '15px',
    height: height ?? '48px',

    '& fieldset': {
      border: '1px solid #99A7AF',
    },
    '&:hover fieldset': {
      borderColor: '#1976d2',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
  },

  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
    minHeight: '39px',
  },

  '& .MuiChip-root': {
    backgroundColor: '#e3f2fd',
    color: '#0d47a1',
    fontSize: '0.75rem',
    fontWeight: 500,
    maxWidth: '100%',
    textOverflow: 'ellipsis',
  },

  '& input::placeholder': {
    fontSize: '15px',
    opacity: 1,
    color: '#667A87',
  },
}));

const OMultiDropdown = <T extends TMultiSelectOption>({
  options,
  value,
  onChange,
  getOptionLabel,
  renderOption: customRenderOption,
  placeholder = 'Search...',
  disabled = false,
  label,
  height,
  placement,
}: IOMultiDropdownProps<T>): JSX.Element => {
  const ALL_OPTION = { value: 'ALL_OPTION', label: 'All' } as T;

  const sortedOptions = useMemo(() => {
    const selectedValuesSet = new Set(value.map((v) => v.value));
    const normalOptions = options.filter((opt) => opt.value !== ALL_OPTION.value);

    const selected = normalOptions.filter((opt) => selectedValuesSet.has(opt.value));
    const unselected = normalOptions.filter((opt) => !selectedValuesSet.has(opt.value));

    return [ALL_OPTION, ...selected, ...unselected];
  }, [options, value]);

  return (
    <div className={styles.multiDropdownContainer}>
      <label className={styles.label}>{label}</label>
      <StyledAutocomplete
        height={height}
        multiple
        disableCloseOnSelect
        options={sortedOptions}
        value={value}
        popupIcon={<BsChevronDown size={19} color="#0088DD" />}
        onChange={(_, newValue) => {
          const isSelectingAll = newValue.some((v) => v.value === ALL_OPTION.value);
          const allSelected = value.length === options.length;

          if (isSelectingAll) {
            onChange(allSelected ? [] : options);
          } else {
            onChange(newValue as T[]);
          }
        }}
        isOptionEqualToValue={(option, val) => {
          return (option as T).value === (val as T).value;
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} variant="outlined" disabled={disabled} />
        )}
        renderOption={(props, option, { selected }) => {
          const typedOption = option as T;
          const isAllOption = typedOption.value === ALL_OPTION.value;
          const allSelected = value.length === options.length;

          return (
            <li {...props} key={typedOption.value} className={styles.optionItem}>
              <div className={styles.optionContent}>
                {isAllOption
                  ? 'All'
                  : customRenderOption
                    ? customRenderOption(typedOption)
                    : getOptionLabel(typedOption)}
              </div>
              {(isAllOption ? allSelected : selected) && <IoCheckmark color="#0088DD" size={24} />}
            </li>
          );
        }}
        renderTags={(selectedOptions, getTagProps) => {
          const isAllSelected = selectedOptions.length === options.length;

          if (isAllSelected) {
            return [<Chip key="all" label="All" size="small" onDelete={() => onChange([])} />];
          }

          return [<Chip key="count" label={`${selectedOptions.length}`} size="small" onDelete={() => onChange([])} />];
        }}
        noOptionsText="No results found"
        slotProps={{
          popper: {
            modifiers: [{ name: 'flip', enabled: false }],
            sx: {
              overflow: 'hidden',
            },
            placement: placement,
          },
          paper: {
            sx: {
              maxHeight: '300px',
              mt: 1,
              borderRadius: 2,
              border: '1px solid #CCD3D7',
              backgroundColor: 'white',
              overflowY: 'auto',
              '& .MuiAutocomplete-listbox': {
                maxHeight: 'none',
                overflow: 'visible',
              },
            },
          },
        }}
      />
    </div>
  );
};

export default OMultiDropdown;
